import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const CashShiftIdSchema = Type.String({ minLength: 1, maxLength: 128 });
const CashShiftNoteSchema = Type.String({ minLength: 2, maxLength: 500 });
const CashShiftSha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const CashShiftDateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\\.[0-9]{3}Z$",
});
const CashShiftMoneyFenSchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const CashShiftNonNegativeMoneyFenSchema = Type.Integer({
  minimum: 0,
  maximum: Number.MAX_SAFE_INTEGER,
});

export const CASH_SHIFT_DIFFERENCE_MANAGER_AMOUNT_THRESHOLD_FEN = 2_000;
export const CASH_SHIFT_DIFFERENCE_OWNER_AMOUNT_THRESHOLD_FEN = 10_000;
export const CASH_SHIFT_DIFFERENCE_MANAGER_RATE_THRESHOLD_BPS = 50;
export const CASH_SHIFT_DIFFERENCE_OWNER_RATE_THRESHOLD_BPS = 200;
export const CASH_SHIFT_DIFFERENCE_POLICY_VERSION = 1;
export const CASH_SHIFT_DIFFERENCE_POLICY_CONTENT_SHA256 =
  "90068d20beca58100f1c8e48ff74ff420a83dc82c114c1fbf7de8e0e9823b28f";

export const CashShiftDifferenceTierSchema = Type.Union([
  Type.Literal("BALANCED"),
  Type.Literal("MANAGER_NOTICE"),
  Type.Literal("MANAGER_OR_OWNER_APPROVAL"),
  Type.Literal("OWNER_APPROVAL"),
]);
export type CashShiftDifferenceTier = Static<typeof CashShiftDifferenceTierSchema>;

export const CashShiftDifferenceApprovalRequirementSchema = Type.Union([
  Type.Literal("NONE"),
  Type.Literal("MANAGER_OR_OWNER"),
  Type.Literal("OWNER"),
]);
export type CashShiftDifferenceApprovalRequirement = Static<
  typeof CashShiftDifferenceApprovalRequirementSchema
>;

export const CashShiftDifferenceAssessmentSchema = Type.Object(
  {
    expectedCashFen: CashShiftMoneyFenSchema,
    countedCashFen: CashShiftNonNegativeMoneyFenSchema,
    cashDifferenceFen: CashShiftMoneyFenSchema,
    absoluteDifferenceFen: CashShiftNonNegativeMoneyFenSchema,
    differenceRateBasisPointsCeiling: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    tier: CashShiftDifferenceTierSchema,
    approvalRequirement: CashShiftDifferenceApprovalRequirementSchema,
    managerNotificationRequired: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type CashShiftDifferenceAssessment = Static<typeof CashShiftDifferenceAssessmentSchema>;

export function assessCashShiftDifference(
  expectedCashFen: number,
  countedCashFen: number,
): CashShiftDifferenceAssessment | null {
  if (
    !Number.isSafeInteger(expectedCashFen) ||
    !Number.isSafeInteger(countedCashFen) ||
    countedCashFen < 0
  ) {
    return null;
  }
  const differenceBigInt = BigInt(countedCashFen) - BigInt(expectedCashFen);
  const maximumSafe = BigInt(Number.MAX_SAFE_INTEGER);
  if (differenceBigInt > maximumSafe || differenceBigInt < -maximumSafe) return null;
  const absoluteDifferenceBigInt = differenceBigInt < 0n ? -differenceBigInt : differenceBigInt;
  const absoluteExpectedBigInt = BigInt(expectedCashFen < 0 ? -expectedCashFen : expectedCashFen);
  const cashDifferenceFen = Number(differenceBigInt);
  const absoluteDifferenceFen = Number(absoluteDifferenceBigInt);
  const differenceRateBasisPointsCeiling =
    absoluteExpectedBigInt === 0n
      ? null
      : Number(
          (absoluteDifferenceBigInt * 10_000n + absoluteExpectedBigInt - 1n) /
            absoluteExpectedBigInt >
            maximumSafe
            ? maximumSafe
            : (absoluteDifferenceBigInt * 10_000n + absoluteExpectedBigInt - 1n) /
                absoluteExpectedBigInt,
        );
  const exceedsManagerRate =
    absoluteExpectedBigInt === 0n
      ? absoluteDifferenceBigInt > 0n
      : absoluteDifferenceBigInt * 10_000n >
        absoluteExpectedBigInt * BigInt(CASH_SHIFT_DIFFERENCE_MANAGER_RATE_THRESHOLD_BPS);
  const exceedsOwnerRate =
    absoluteExpectedBigInt === 0n
      ? absoluteDifferenceBigInt > 0n
      : absoluteDifferenceBigInt * 10_000n >
        absoluteExpectedBigInt * BigInt(CASH_SHIFT_DIFFERENCE_OWNER_RATE_THRESHOLD_BPS);

  let tier: CashShiftDifferenceTier;
  let approvalRequirement: CashShiftDifferenceApprovalRequirement;
  if (absoluteDifferenceFen === 0) {
    tier = "BALANCED";
    approvalRequirement = "NONE";
  } else if (
    absoluteDifferenceFen > CASH_SHIFT_DIFFERENCE_OWNER_AMOUNT_THRESHOLD_FEN ||
    exceedsOwnerRate
  ) {
    tier = "OWNER_APPROVAL";
    approvalRequirement = "OWNER";
  } else if (
    absoluteDifferenceFen > CASH_SHIFT_DIFFERENCE_MANAGER_AMOUNT_THRESHOLD_FEN ||
    exceedsManagerRate
  ) {
    tier = "MANAGER_OR_OWNER_APPROVAL";
    approvalRequirement = "MANAGER_OR_OWNER";
  } else {
    tier = "MANAGER_NOTICE";
    approvalRequirement = "NONE";
  }

  return {
    expectedCashFen,
    countedCashFen,
    cashDifferenceFen,
    absoluteDifferenceFen,
    differenceRateBasisPointsCeiling,
    tier,
    approvalRequirement,
    managerNotificationRequired: tier === "MANAGER_NOTICE",
  };
}

export const CashShiftActorSummarySchema = Type.Object(
  {
    accountId: CashShiftIdSchema,
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
  },
  { additionalProperties: false },
);
export type CashShiftActorSummary = Static<typeof CashShiftActorSummarySchema>;

export const CashShiftHandoverModeSchema = Type.Union([
  Type.Literal("OPENING_CASHIER"),
  Type.Literal("MANAGER_SUBSTITUTE"),
]);
export type CashShiftHandoverMode = Static<typeof CashShiftHandoverModeSchema>;

export const CashShiftReceiveModeSchema = Type.Union([
  Type.Literal("NEXT_CASHIER"),
  Type.Literal("MANAGER_SUBSTITUTE"),
]);
export type CashShiftReceiveMode = Static<typeof CashShiftReceiveModeSchema>;

export const PreviewCashShiftHandoverRequestSchema = Type.Object(
  {
    countedCashFen: CashShiftNonNegativeMoneyFenSchema,
    handoverNote: CashShiftNoteSchema,
    substituteReason: Type.Union([CashShiftNoteSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type PreviewCashShiftHandoverRequest = Static<typeof PreviewCashShiftHandoverRequestSchema>;

export const CashShiftHandoverPreviewReadModelSchema = Type.Object(
  {
    contractVersion: Type.Literal(1),
    shiftId: CashShiftIdSchema,
    shiftVersion: Type.Integer({ minimum: 1 }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    openingCashFen: CashShiftNonNegativeMoneyFenSchema,
    cashReceiptsFen: CashShiftNonNegativeMoneyFenSchema,
    cashRefundsFen: CashShiftNonNegativeMoneyFenSchema,
    cashInFen: CashShiftNonNegativeMoneyFenSchema,
    cashOutFen: CashShiftNonNegativeMoneyFenSchema,
    assessment: CashShiftDifferenceAssessmentSchema,
    openedBy: CashShiftActorSummarySchema,
    handoverActor: CashShiftActorSummarySchema,
    handoverMode: CashShiftHandoverModeSchema,
    handoverNote: CashShiftNoteSchema,
    substituteReason: Type.Union([CashShiftNoteSchema, Type.Null()]),
    openTableCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    inventoryDifferenceCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    generatedAt: CashShiftDateTimeSchema,
    previewSha256: CashShiftSha256Schema,
  },
  { additionalProperties: false },
);
export type CashShiftHandoverPreviewReadModel = Static<
  typeof CashShiftHandoverPreviewReadModelSchema
>;

export const SubmitCashShiftHandoverCommandSchema = Type.Object(
  {
    expectedShiftVersion: Type.Integer({ minimum: 1 }),
    countedCashFen: CashShiftNonNegativeMoneyFenSchema,
    handoverNote: CashShiftNoteSchema,
    substituteReason: Type.Union([CashShiftNoteSchema, Type.Null()]),
    previewSha256: CashShiftSha256Schema,
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type SubmitCashShiftHandoverCommand = Static<typeof SubmitCashShiftHandoverCommandSchema>;

export const ReceiveCashShiftHandoverCommandSchema = Type.Object(
  {
    expectedHandoverVersion: Type.Integer({ minimum: 1 }),
    receiveNote: CashShiftNoteSchema,
    substituteReason: Type.Union([CashShiftNoteSchema, Type.Null()]),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type ReceiveCashShiftHandoverCommand = Static<typeof ReceiveCashShiftHandoverCommandSchema>;

export const CashShiftHandoverWorkflowStatusSchema = Type.Union([
  Type.Literal("WAITING_RECEIVER"),
  Type.Literal("WAITING_APPROVAL"),
  Type.Literal("APPROVAL_READY_TO_CLOSE"),
  Type.Literal("APPROVAL_REJECTED"),
  Type.Literal("CLOSED"),
]);
export type CashShiftHandoverWorkflowStatus = Static<typeof CashShiftHandoverWorkflowStatusSchema>;

export const CashShiftHandoverSubmissionResultSchema = Type.Object(
  {
    kind: Type.Literal("CASH_SHIFT_HANDOVER_SUBMITTED"),
    workflowStatus: Type.Literal("WAITING_RECEIVER"),
    shiftId: CashShiftIdSchema,
    shiftVersion: Type.Integer({ minimum: 2 }),
    assessment: CashShiftDifferenceAssessmentSchema,
    handoverActor: CashShiftActorSummarySchema,
    handoverMode: CashShiftHandoverModeSchema,
    handoverNote: CashShiftNoteSchema,
    substituteReason: Type.Union([CashShiftNoteSchema, Type.Null()]),
    recordedAt: CashShiftDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CashShiftHandoverSubmissionResult = Static<
  typeof CashShiftHandoverSubmissionResultSchema
>;

export const CashShiftHandoverReceiveResultSchema = Type.Object(
  {
    kind: Type.Literal("CASH_SHIFT_RECEIVE_ACCEPTED"),
    workflowStatus: Type.Union([Type.Literal("WAITING_APPROVAL"), Type.Literal("CLOSED")]),
    shiftId: CashShiftIdSchema,
    shiftVersion: Type.Integer({ minimum: 3 }),
    assessment: CashShiftDifferenceAssessmentSchema,
    receiver: CashShiftActorSummarySchema,
    receiveMode: CashShiftReceiveModeSchema,
    receiveNote: CashShiftNoteSchema,
    substituteReason: Type.Union([CashShiftNoteSchema, Type.Null()]),
    approvalId: Type.Union([CashShiftIdSchema, Type.Null()]),
    approvalRequirement: CashShiftDifferenceApprovalRequirementSchema,
    managerNotificationRecorded: Type.Boolean(),
    recordedAt: CashShiftDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CashShiftHandoverReceiveResult = Static<typeof CashShiftHandoverReceiveResultSchema>;

export const CashShiftHandoverWorkflowReadModelSchema = Type.Object(
  {
    contractVersion: Type.Literal(1),
    shiftId: CashShiftIdSchema,
    shiftVersion: Type.Integer({ minimum: 2 }),
    workflowStatus: CashShiftHandoverWorkflowStatusSchema,
    assessment: CashShiftDifferenceAssessmentSchema,
    handoverActor: CashShiftActorSummarySchema,
    handoverMode: CashShiftHandoverModeSchema,
    handoverNote: CashShiftNoteSchema,
    handoverSubstituteReason: Type.Union([CashShiftNoteSchema, Type.Null()]),
    handoverSubmittedAt: CashShiftDateTimeSchema,
    receiver: Type.Union([CashShiftActorSummarySchema, Type.Null()]),
    receiveMode: Type.Union([CashShiftReceiveModeSchema, Type.Null()]),
    receiveNote: Type.Union([CashShiftNoteSchema, Type.Null()]),
    receiveSubstituteReason: Type.Union([CashShiftNoteSchema, Type.Null()]),
    receivedAt: Type.Union([CashShiftDateTimeSchema, Type.Null()]),
    approvalId: Type.Union([CashShiftIdSchema, Type.Null()]),
    approvalRequirement: CashShiftDifferenceApprovalRequirementSchema,
    approvalStatus: Type.Union([
      Type.Literal("NOT_REQUIRED"),
      Type.Literal("PENDING"),
      Type.Literal("APPROVED"),
      Type.Literal("REJECTED"),
      Type.Literal("EXECUTION_OUTCOME_UNKNOWN"),
      Type.Literal("SUCCEEDED"),
    ]),
    managerNotificationRecorded: Type.Boolean(),
    closedAt: Type.Union([CashShiftDateTimeSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type CashShiftHandoverWorkflowReadModel = Static<
  typeof CashShiftHandoverWorkflowReadModelSchema
>;

export function isCashShiftHandoverSubmissionResult(
  value: unknown,
): value is CashShiftHandoverSubmissionResult {
  return (
    Value.Check(CashShiftHandoverSubmissionResultSchema, value) &&
    isCashShiftDifferenceAssessment((value as CashShiftHandoverSubmissionResult).assessment)
  );
}

export function isCashShiftHandoverReceiveResult(
  value: unknown,
): value is CashShiftHandoverReceiveResult {
  if (!Value.Check(CashShiftHandoverReceiveResultSchema, value)) return false;
  const result = value as CashShiftHandoverReceiveResult;
  if (!isCashShiftDifferenceAssessment(result.assessment)) return false;
  return result.workflowStatus === "CLOSED"
    ? result.approvalId === null &&
        result.approvalRequirement === "NONE" &&
        result.managerNotificationRecorded === result.assessment.managerNotificationRequired
    : result.approvalId !== null &&
        result.approvalRequirement === result.assessment.approvalRequirement &&
        result.approvalRequirement !== "NONE" &&
        !result.managerNotificationRecorded;
}

export function isCashShiftHandoverWorkflowReadModel(
  value: unknown,
): value is CashShiftHandoverWorkflowReadModel {
  if (!Value.Check(CashShiftHandoverWorkflowReadModelSchema, value)) return false;
  const workflow = value as CashShiftHandoverWorkflowReadModel;
  if (!isCashShiftDifferenceAssessment(workflow.assessment)) return false;
  const hasReceiver = workflow.receiver !== null;
  if (
    hasReceiver !== (workflow.receiveMode !== null) ||
    hasReceiver !== (workflow.receiveNote !== null) ||
    hasReceiver !== (workflow.receivedAt !== null)
  ) {
    return false;
  }
  if (workflow.receiveMode === "NEXT_CASHIER" && workflow.receiveSubstituteReason !== null) {
    return false;
  }
  if (workflow.receiveMode === "MANAGER_SUBSTITUTE" && workflow.receiveSubstituteReason === null) {
    return false;
  }
  if (workflow.workflowStatus === "WAITING_RECEIVER") {
    return (
      !hasReceiver &&
      workflow.approvalId === null &&
      workflow.approvalStatus === "NOT_REQUIRED" &&
      workflow.closedAt === null
    );
  }
  if (workflow.workflowStatus === "CLOSED") {
    return (
      hasReceiver &&
      workflow.closedAt !== null &&
      (workflow.approvalRequirement === "NONE"
        ? workflow.approvalId === null && workflow.approvalStatus === "NOT_REQUIRED"
        : workflow.approvalId !== null && workflow.approvalStatus === "SUCCEEDED")
    );
  }
  return (
    hasReceiver &&
    workflow.closedAt === null &&
    workflow.approvalId !== null &&
    workflow.approvalRequirement !== "NONE"
  );
}

export const CashShiftDifferenceApprovalSnapshotSchema = Type.Object(
  {
    kind: Type.Literal("CASH_SHIFT_DIFFERENCE"),
    snapshotVersion: Type.Literal(1),
    shiftId: CashShiftIdSchema,
    originalDocumentLabel: Type.String({ minLength: 2, maxLength: 120 }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    shiftVersion: Type.Integer({ minimum: 1 }),
    openingCashFen: CashShiftNonNegativeMoneyFenSchema,
    cashReceiptsFen: CashShiftNonNegativeMoneyFenSchema,
    cashRefundsFen: CashShiftNonNegativeMoneyFenSchema,
    cashInFen: CashShiftNonNegativeMoneyFenSchema,
    cashOutFen: CashShiftNonNegativeMoneyFenSchema,
    assessment: CashShiftDifferenceAssessmentSchema,
    openedBy: CashShiftActorSummarySchema,
    openedByPersonId: Type.String({ minLength: 1, maxLength: 128 }),
    handoverActor: CashShiftActorSummarySchema,
    handoverActorPersonId: Type.String({ minLength: 1, maxLength: 128 }),
    receiver: CashShiftActorSummarySchema,
    receiverPersonId: Type.String({ minLength: 1, maxLength: 128 }),
    handoverMode: CashShiftHandoverModeSchema,
    receiveMode: CashShiftReceiveModeSchema,
    handoverNote: CashShiftNoteSchema,
    handoverSubstituteReason: Type.Union([CashShiftNoteSchema, Type.Null()]),
    receiveNote: CashShiftNoteSchema,
    receiveSubstituteReason: Type.Union([CashShiftNoteSchema, Type.Null()]),
    previewSha256: CashShiftSha256Schema,
    commandSha256: CashShiftSha256Schema,
    capturedAt: CashShiftDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CashShiftDifferenceApprovalSnapshot = Static<
  typeof CashShiftDifferenceApprovalSnapshotSchema
>;

export function isCashShiftDifferenceAssessment(
  value: unknown,
): value is CashShiftDifferenceAssessment {
  if (!Value.Check(CashShiftDifferenceAssessmentSchema, value)) return false;
  const assessment = value as CashShiftDifferenceAssessment;
  const expected = assessCashShiftDifference(assessment.expectedCashFen, assessment.countedCashFen);
  return expected !== null && JSON.stringify(expected) === JSON.stringify(assessment);
}

export function isCashShiftHandoverPreviewReadModel(
  value: unknown,
): value is CashShiftHandoverPreviewReadModel {
  return (
    Value.Check(CashShiftHandoverPreviewReadModelSchema, value) &&
    isCashShiftDifferenceAssessment((value as CashShiftHandoverPreviewReadModel).assessment)
  );
}

export function isCashShiftDifferenceApprovalSnapshot(
  value: unknown,
): value is CashShiftDifferenceApprovalSnapshot {
  if (!Value.Check(CashShiftDifferenceApprovalSnapshotSchema, value)) return false;
  const snapshot = value as CashShiftDifferenceApprovalSnapshot;
  const handoverResponsibilityMatches =
    snapshot.handoverMode === "OPENING_CASHIER"
      ? snapshot.openedBy.accountId === snapshot.handoverActor.accountId &&
        snapshot.openedByPersonId === snapshot.handoverActorPersonId &&
        snapshot.handoverSubstituteReason === null
      : snapshot.openedBy.accountId !== snapshot.handoverActor.accountId &&
        snapshot.openedByPersonId !== snapshot.handoverActorPersonId &&
        snapshot.handoverSubstituteReason !== null;
  const receiveResponsibilityMatches =
    snapshot.receiveMode === "NEXT_CASHIER"
      ? snapshot.receiveSubstituteReason === null
      : snapshot.receiveSubstituteReason !== null;
  return (
    isCashShiftDifferenceAssessment(snapshot.assessment) &&
    snapshot.assessment.approvalRequirement !== "NONE" &&
    snapshot.shiftVersion >= 3 &&
    snapshot.receiver.accountId !== snapshot.handoverActor.accountId &&
    snapshot.receiverPersonId !== snapshot.handoverActorPersonId &&
    handoverResponsibilityMatches &&
    receiveResponsibilityMatches
  );
}
